<template>
  <div class="shopping-list">
    <h2 class="title">{{ $t("Shopping List") }}</h2>
    <TutorialBox id="shoppingList">
      <p>{{ $t("Here you check off your shopped items.") }}</p>
      <p>{{ $t("tutorials.list") }}</p>
      <p>
        <i18n path="tutorials.createRecipes">
          <router-link to="/recipes">{{ $t("Recipes") }}</router-link>
        </i18n>
      </p>
    </TutorialBox>
    <div
      v-for="cluster in smartClusteredEntries"
      :key="cluster.entries[0]._id"
      class="panel shopping-list--cluster"
    >
      <h3
        class="panel-heading shopping-list--shop-name"
        v-if="cluster.entries[0] && cluster.centroidPosition()"
      >
        <NearestShop :position="cluster.centroidPosition()" />
      </h3>
      <ShoppingListEntry
        v-for="entry in cluster.entries"
        :entry="entry"
        :allShops="allShops"
        :favoriteShops="currentlyPresentShops"
        v-bind:key="entry._id"
      />
    </div>
    <p v-if="!hasEntries" class="shopping-list--empty-hint">
      {{ $t("Your shopping list is empty.") }}
    </p>
    <shopping-list-manual-entry-add class="is-hidden-print" />
  </div>
</template>

<script>
import Vue from "vue";
import { Shop } from "@/models/Shop";
import { ShoppingList } from "@/models/ShoppingList.ts";
import SmartOrderShoppingList from "@/models/SmartOrderShoppingList";
import { orderEntriesByBestRoute } from "@/models/SmartOrderInsideShop";
import { haversineDistance } from "@/models/Geolocation";
import ShoppingListManualEntryAdd from "@/components/ShoppingListManualEntryAdd.vue";
import TutorialBox from "@/components/TutorialBox.vue";
import NearestShop from "@/components/NearestShop.vue";
import ShoppingListEntry from "@/components/ShoppingListEntry.vue";

export default Vue.extend({
  name: "ShoppingList",
  data() {
    return {};
  },
  methods: {},
  computed: {
    entries: function () {
      return [
        ...this.$store.state.shoppingList.entries,
        ...this.$store.state.shoppingList.historicEntries,
      ];
    },
    currentEntries: function () {
      const ONE_HOUR = 60 * 60 * 1000;
      return this.entries.filter((entry) => {
        return (
          (entry.shoppedAt === null && entry.isShopped === false) ||
          (entry.shoppedAt && new Date() - entry.shoppedAt < ONE_HOUR * 24)
        );
      });
    },
    hasEntries: function () {
      return this.currentEntries.length > 0;
    },
    smartClusteredEntries: function () {
      const list = new SmartOrderShoppingList(this.currentEntries);
      let clusters = list.clustered();

      clusters.forEach((cluster) => {
        const centroidPosition = cluster.centroidPosition();
        const relevantHistory = this.entries.filter((historicEntry) => {
          return (
            historicEntry.shoppedAtPosition &&
            centroidPosition &&
            haversineDistance(
              historicEntry.shoppedAtPosition,
              centroidPosition
            ) <= 50
          );
        });

        cluster.entries = orderEntriesByBestRoute(
          cluster.entries,
          relevantHistory
        );
      });
      return clusters;
    },
    allShops() {
      return this.$store.state.shops;
    },
    currentlyPresentShops() {
      const shopsFromClusters = this.smartClusteredEntries
        .map((cluster) => cluster.centroidPosition())
        .filter((x) => x)
        .map((position) => Shop.matchByPosition(this.allShops, position))
        .filter((shop) => shop);
      return shopsFromClusters;
    },
  },
  watch: {},
  components: {
    ShoppingListEntry,
    ShoppingListManualEntryAdd,
    TutorialBox,
    NearestShop,
  },
});
</script>

<style scoped>
.shopping-list--shop-name {
  font-size: 1em;
}

.shopping-list--empty-hint {
  margin-bottom: 1.5rem;
}

@media print {
  .shopping-list--shop-name {
    background-color: #fff;
  }

  .panel {
    box-shadow: none;
  }

  .dropdown {
    display: none;
  }
}
</style>
<i18n>
{
  "en": {
    "Move entry to": "Move entry to {shop}",
    "tutorials": {
      "list": "You can add as many entries as you like. Check them off whenever you bought an item. Checked-off entries will disappear automatically the next day.",
      "createRecipes": "You can enter your favorite {0} in order to save time."
    }
  },
  "de": {
    "for": "für",
    "Your shopping list is empty.": "Die Einkaufsliste ist leer.",
    "Delete": "Löschen",
    "Here you check off your shopped items.": "Hier hakst du die Dinge ab.",
    "Move entry to": "Verschieben zu {shop}",
    "Recipes": "Rezepte",
    "tutorials": {
      "list":"Du kannst beliebig viele Einträge hinzufügen. Hake sie ab, wenn du sie gekauft hast. Die erledigten Einträge verschwinden am nächsten Tag automatisch.",
      "createRecipes": "Trage deine {0} ein, um die Zutaten schneller hinzuzufügen."
    }
  }
}
</i18n>
